import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api } from "../api";
import { signOut } from "../amplify";
import { Input, Label } from "./ui";
import { Button } from "./ui/Button";

export default function DangerZone({ email }: { email: string }) {
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function onDelete() {
    if (confirm.trim().toLowerCase() !== email.trim().toLowerCase()) {
      setErr("That doesn't match your email.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.deleteMe(confirm.trim());
      await signOut();
      nav("/", { replace: true });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "delete failed");
      setBusy(false);
    }
  }

  return (
    <div className="card border border-terracotta/30 bg-terracotta/5">
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-terracotta shrink-0 mt-1" />
        <div className="flex-1">
          <h2 className="font-serif text-xl mb-1">Delete account</h2>
          <p className="text-sm text-ink-soft leading-relaxed">
            This erases your profile, vocabulary, messages, and usage history — permanently and immediately.
            The tutor stops pinging you. You can always start over with a new signup, but none of your old progress
            comes back.
          </p>

          {!open ? (
            <div className="mt-4">
              <Button variant="outline" onClick={() => setOpen(true)}>
                I want to delete my account
              </Button>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <Label>
                Type <span className="font-mono">{email}</span> to confirm
              </Label>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={email}
                autoComplete="off"
              />
              {err && <p className="text-sm text-terracotta font-medium">{err}</p>}
              <div className="flex gap-3 pt-1">
                <Button
                  onClick={onDelete}
                  disabled={busy || confirm.trim().toLowerCase() !== email.trim().toLowerCase()}
                  className="bg-terracotta hover:bg-terracotta-soft"
                >
                  {busy ? "Deleting…" : "Delete everything"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setOpen(false); setConfirm(""); setErr(null); }}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
